/**
 * Sub-spec 09 Plan 7 — /settings/integrations/cameras
 *
 * Per-facility camera streams. The page lets staff pick a facility from a
 * dropdown, add/edit/delete streams, and re-attest on save. Pro+ tier.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Facility {
  id: string;
  name: string;
}

interface CameraStream {
  id: string;
  name: string;
  kind: 'iframe' | 'hls';
  url: string;
}

interface StreamsResponse {
  streams: CameraStream[];
  attestationAt: string | null;
}

function newStubId(): string {
  // The server replaces blank/invalid ids on save; this is just a stable
  // client key for the form rows.
  return `local-${Math.random().toString(36).slice(2, 10)}`;
}

export function FacilityCamerasPage() {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>('');
  const [streams, setStreams] = useState<CameraStream[]>([]);
  const [attestation, setAttestation] = useState(false);
  const [attestationAt, setAttestationAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load facilities once.
  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch('/api/me', { credentials: 'include' });
        if (!res.ok) {
          setError('Unable to load facilities.');
          return;
        }
        const body = (await res.json()) as { facilities?: Facility[] };
        const fac = body.facilities ?? [];
        setFacilities(fac);
        if (fac[0]) setSelectedFacilityId(fac[0].id);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load streams when facility changes.
  const loadStreams = useCallback(async (facilityId: string) => {
    setError(null);
    const res = await fetch(`/api/facilities/${facilityId}/camera-streams`, {
      credentials: 'include',
    });
    if (!res.ok) {
      setError('Unable to load camera streams for this facility.');
      return;
    }
    const body = (await res.json()) as StreamsResponse;
    setStreams(body.streams);
    setAttestationAt(body.attestationAt);
    setAttestation(false);
  }, []);

  useEffect(() => {
    if (selectedFacilityId) void loadStreams(selectedFacilityId);
  }, [selectedFacilityId, loadStreams]);

  const addRow = () => {
    setStreams((prev) => [...prev, { id: newStubId(), name: '', kind: 'iframe', url: '' }]);
  };

  const updateRow = (idx: number, patch: Partial<CameraStream>) => {
    setStreams((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeRow = (idx: number) => {
    setStreams((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = useCallback(async () => {
    if (!selectedFacilityId) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/facilities/${selectedFacilityId}/camera-streams`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ streams, attestation }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string; issues?: string[] };
        setError(err?.issues?.join(', ') ?? err?.error ?? 'Save failed.');
        return;
      }
      await loadStreams(selectedFacilityId);
    } finally {
      setSaving(false);
    }
  }, [selectedFacilityId, streams, attestation, loadStreams]);

  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <header>
        <h1 className="text-xl font-semibold">IP camera embed</h1>
        <p className="text-sm text-muted-foreground">
          Show live camera feeds on your customers' live stay pages. Each facility has its own list
          of streams (max 8). HTTPS only.
        </p>
      </header>

      {error ? (
        <div className="rounded-md bg-warning/10 border border-warning px-3 py-2 text-sm text-warning">
          {error}
        </div>
      ) : null}

      <div>
        <label className="block text-sm font-medium mb-1">Facility</label>
        <select
          value={selectedFacilityId}
          onChange={(e) => setSelectedFacilityId(e.target.value)}
          className="text-sm rounded-md border border-border px-3 py-2"
        >
          {facilities.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>

      <ul className="space-y-2">
        {streams.map((s, idx) => (
          <li
            key={s.id}
            className="rounded-lg border border-border bg-card p-3 grid grid-cols-1 md:grid-cols-12 gap-2 items-center"
          >
            <input
              className="md:col-span-3 text-sm rounded-md border border-border px-2 py-1"
              placeholder="Camera name"
              value={s.name}
              onChange={(e) => updateRow(idx, { name: e.target.value })}
            />
            <select
              className="md:col-span-2 text-sm rounded-md border border-border px-2 py-1"
              value={s.kind}
              onChange={(e) => updateRow(idx, { kind: e.target.value as 'iframe' | 'hls' })}
            >
              <option value="iframe">Iframe (vendor share URL)</option>
              <option value="hls">HLS stream</option>
            </select>
            <input
              className="md:col-span-6 text-sm rounded-md border border-border px-2 py-1"
              placeholder="https://..."
              value={s.url}
              onChange={(e) => updateRow(idx, { url: e.target.value })}
            />
            <button
              type="button"
              className="md:col-span-1 text-xs text-warning underline"
              onClick={() => removeRow(idx)}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="text-sm rounded-md border border-border px-3 py-1.5 hover:bg-secondary"
        onClick={addRow}
        disabled={streams.length >= 8}
      >
        Add stream
      </button>

      <label className="flex items-start gap-2 pt-3">
        <input
          type="checkbox"
          checked={attestation}
          onChange={(e) => setAttestation(e.target.checked)}
        />
        <span className="text-sm text-muted-foreground">
          I attest that I have customer permission to broadcast live video and that streams comply
          with applicable privacy laws (e.g. UK GDPR Article 8 / equivalent).
        </span>
      </label>

      {attestationAt ? (
        <p className="text-xs text-muted-foreground">
          Last attestation recorded:{' '}
          <span className="num">{new Date(attestationAt).toLocaleString()}</span>
        </p>
      ) : null}

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          className="text-sm rounded-md bg-primary text-primary-foreground px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50"
          onClick={() => void save()}
          disabled={saving || (!attestation && streams.length > 0)}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <Link to="/settings/integrations" className="text-sm underline">
          Back to integrations
        </Link>
      </div>
    </div>
  );
}
